import { useLingui } from '@lingui/react'
import _ from 'lodash'
import { useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useLocation, useParams, Link } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'
import { skipToken } from '@reduxjs/toolkit/query'

import { Bars } from 'react-loader-spinner'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import colors from '@/assets/scss/_themes-vars.module.scss'
import ErrorTemplate from '@/components/ui/template/ErrorTemplate/ErrorTemplate'
import PageContainer from '@/components/ui/containers/PageContainer'

import { useGetPatientQuery } from '@/core/services/patient'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { useIntro } from '@/hooks/useIntro'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAlertsByArea } from '@/redux/reducers/alerts.reducer'

import { InfoPatientIntro } from '@/utils/onboarding/intro'

import { Actions } from './Actions'
import { INFO_PATIENT_PAGE_ALERTS } from '../model/constants'
import { useGenerateFileUploadQRCode } from '../model/useGenerateFileUploadQRCode'
import { PatientAlerts } from './PatientAlerts'
import { PatientDetails } from './PatientDetails'

export type PatientProps = {
  patientId?: string
  showInformationTab?: boolean
  showPastConsultationsTab?: boolean
  showDocumentsTab?: boolean
  showFormsTab?: boolean
}

export function InfoPatientPage(props: PatientProps) {
  const { showInformationTab = true, showPastConsultationsTab = true, showDocumentsTab = true, showFormsTab = true } = props
  useLingui()
  const location = useLocation()
  const { patientId: paramPatientId } = useParams()
  const patientId = props.patientId || paramPatientId
  const isDialog = props.patientId ? true : false

  const alerts = useAppSelector((state) => selectAlertsByArea(state, INFO_PATIENT_PAGE_ALERTS))

  const { data: patient, error, isLoading } = useGetPatientQuery(patientId ?? skipToken)

  const [formErrors, setFormErrors] = useState<FieldErrors<PatientProfileFormData>>({})

  useIntro(InfoPatientIntro, !patient && isLoading && !error)
  useGenerateFileUploadQRCode(patientId)

  if (error) {
    return <ErrorTemplate code="NotFoundError" message={t`The patient you are looking for does not exist or you do not have the right to access`} />
  }

  if (isLoading) {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <Bars height="80" width="80" color={colors.primaryMain} />
      </Stack>
    )
  }

  if (!patient) return null

  return (
    <PageContainer noPadding={isDialog}>
      <Stack direction="column" spacing={alerts.length ? 2 : 4}>
        <Stack direction="row" justifyContent="space-between" id="actions">
          {!isDialog && (
            <>
              <Button
                component={Link}
                size="small"
                to="/patients"
                startIcon={<ArrowBackIcon />}
                state={{ from: { pathname: location.pathname, pattern: '/patients/:patientId' } }}
                variant="neutral"
              >
                <Trans>Back</Trans>
              </Button>
              <Actions patientId={patient._id} setFormErrors={setFormErrors} />
            </>
          )}
        </Stack>
        <PatientAlerts patientId={patient._id} />
        <PatientDetails
          errors={formErrors}
          patient={patient}
          showInformationTab={showInformationTab}
          showPastConsultationsTab={showPastConsultationsTab}
          showDocumentsTab={showDocumentsTab}
          showFormsTab={showFormsTab}
        />
      </Stack>
    </PageContainer>
  )
}
